import React, { useState, useEffect } from "react";
import KpiCard from "../components/Dashboard/KpiCard";
import DonutChart from "../components/Charts/DonutChart";
import BarChart from "../components/Charts/BarChart";
import { post, get } from "../utils/api";

// ── Lucide icons ────────────────────────────────────────────────────────────
import { Users, Layers, Briefcase, UserPlus } from "lucide-react";

// ── Default sample data (replaced by API data when connected) ────────────────
const FUND_COLORS = [
    "#6366F1", // indigo
    "#F59E0B", // amber
    "#10B981", // emerald
    "#EF4444", // red
    "#3B82F6", // blue
    "#8B5CF6", // violet
    "#F97316", // orange
];

const SAMPLE_FUND_DATA = {
    labels: ["HF CAT II", "HF CAT III", "PG2"],
    data: [2, 1, 1],
    colors: FUND_COLORS,
    legendItems: [
        { label: "HF CAT II", value: "2 funds", color: "#6366F1" },
        { label: "HF CAT III", value: "1 fund", color: "#F59E0B" },
        { label: "PG2", value: "1 fund", color: "#10B981" },
    ],
};

const ROLE_COLORS = ["#6366F1", "#F97316", "#8B5CF6", "#EF4444", "#10B981"];

const SAMPLE_ROLE_DATA = {
    labels: ["DISTRIBUTOR", "ADMIN", "CHECKER", "SALES", "SALES HEAD"],
    data: [4, 2, 1, 1, 1],
};



// ── Component ──────────────────────────────────────────────────────────────
const AdminOverviewDashboard = () => {
    const userData = JSON.parse(localStorage.getItem("user_details") || "{}");

    const [kpiData, setKpiData] = useState({
        totalUsers: { value: 0, trend: 0, subtitle: "Loading..." },
        totalFunds: { value: 0, trend: 0, subtitle: "Loading..." },
        distributors: { value: 0, trend: 0, subtitle: "Loading..." },
        monthlyOnboardedDistributors: { value: 0, trend: 0, subtitle: "Loading..." },
    });


    const [fundData, setFundData] = useState(SAMPLE_FUND_DATA);
    const [roleData, setRoleData] = useState(SAMPLE_ROLE_DATA);
    const [fundCenterCount, setFundCenterCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState("...");

    const formatTimestamp = () => {
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const formattedTime = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return `${formattedDate} – ${formattedTime}`;
    };

    useEffect(() => {
        setLastUpdated(formatTimestamp());
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const [
                usersRes,
                fundsRes,
                distributorsRes,
                monthlyOnboardedDistributorsRes,
                rolewiseRes,
                fundDistRes,
                fundCenterRes
            ] = await Promise.all([
                get("/admindashboard/GetTotalActiveUsers_Count"),
                get("/admindashboard/GetTotalFund_Details_Count"),
                get("/admindashboard/GetTotalDistributor_Details_Count"),
                get("/admindashboard/GetCurrentMonthOnboardedDistributor_Count"),
                get("/admindashboard/GetTotalUsers_Rolewise_Count"),
                get("/admindashboard/Get_FundDistribution_Count"),
                get("/admindashboard/Get_FundDistribution_CenterCount")
            ]);

            const newKpiData = { ...kpiData };

            if (usersRes.data.Success) {
                newKpiData.totalUsers.value = usersRes.data.Data[0]?.total_user_count || 0;
            }
            if (fundsRes.data.Success) {
                newKpiData.totalFunds.value = fundsRes.data.Data[0]?.total_fund_details_count || 0;
            }
            if (distributorsRes.data.Success) {
                newKpiData.distributors.value = distributorsRes.data.Data[0]?.total_distributor_details_count || 0;
            }
            if (monthlyOnboardedDistributorsRes.data.Success) {
                newKpiData.monthlyOnboardedDistributors.value =
                    monthlyOnboardedDistributorsRes.data.Data[0]?.current_month_onboarded_distributor_count || 0;
            }

            if (rolewiseRes.data.Success && rolewiseRes.data.Data.length > 0) {
                const roles = rolewiseRes.data.Data;
                setRoleData({
                    labels: roles.map(r => r.role_desc || String(r.role_id)),
                    data: roles.map(r => parseInt(r.active_role_count)),
                });
                newKpiData.totalUsers.subtitle = `Active across ${roles.length} roles`;
            } else {
                newKpiData.totalUsers.subtitle = "Active system users";
            }

            if (fundDistRes.data.Success) {
                const funds = fundDistRes.data.Data;

                setFundData({
                    labels: funds.map(f => f.fund_name || f.fund_code || String(f.fund_id)),
                    data: funds.map(f => parseInt(f.fund_count)),
                    colors: FUND_COLORS,
                    legendItems: funds.map((f, i) => ({
                        label: f.fund_name || f.fund_code || String(f.fund_id),
                        value: `${f.fund_count} distributors`,
                        color: FUND_COLORS[i % FUND_COLORS.length],
                        details: f.class_details ? `Classes: ${f.class_details}` : "Classes: -",
                    })),
                });
                newKpiData.totalFunds.subtitle = "AIF & PMS products";
            } else {
                newKpiData.totalFunds.subtitle = "AIF & PMS products";
            }

            if (fundCenterRes.data.Success) {
                setFundCenterCount(
                    parseInt(fundCenterRes.data.Data?.[0]?.total_active_fund_count || 0, 10)
                );
            } else {
                setFundCenterCount(0);
            }

            newKpiData.distributors.subtitle = "With signed agreements";
            newKpiData.monthlyOnboardedDistributors.subtitle = "Approved and signed this month";

            setKpiData(newKpiData);
            setLastUpdated(formatTimestamp());
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const roleBarDatasets = [
        {
            label: "Users",
            data: roleData.data,
            backgroundColor: roleData.labels.map((_, i) => ROLE_COLORS[i % ROLE_COLORS.length]),
        },
    ];

    return (
        <div className="admin-overview">
            {/* Page Header */}
            <div className="admin-overview-header">
                <div>
                    <h1 className="admin-overview-title">Dashboard Overview</h1>
                    <p className="admin-overview-sub">
                        Welcome back! Here's your system overview
                    </p>
                </div>
                <span className="admin-overview-updated">
                    Last updated:&nbsp;<strong>{lastUpdated}</strong>
                </span>
            </div>

            {/* KPI Cards */}
            <div className="admin-kpi-grid">
                <KpiCard
                    title="Total Users"
                    value={kpiData.totalUsers.value}
                    subtitle={kpiData.totalUsers.subtitle}
                    trend={kpiData.totalUsers.trend}
                    icon={<Users size={22} strokeWidth={2.5} />}
                    iconBgColor="#6366F1"
                    iconColor="#fff"
                />
                <KpiCard
                    title="Total Funds"
                    value={kpiData.totalFunds.value}
                    subtitle={kpiData.totalFunds.subtitle}
                    trend={kpiData.totalFunds.trend}
                    icon={<Briefcase size={22} strokeWidth={2.5} />}
                    iconBgColor="#8B5CF6"
                    iconColor="#fff"
                />
                <KpiCard
                    title="Distributors"
                    value={kpiData.distributors.value}
                    subtitle={kpiData.distributors.subtitle}
                    trend={kpiData.distributors.trend}
                    icon={<Layers size={22} strokeWidth={2.5} />}
                    iconBgColor="#EC4899"
                    iconColor="#fff"
                />
                <KpiCard
                    title="Monthly Onboarded Distributor"
                    value={kpiData.monthlyOnboardedDistributors.value}
                    subtitle={kpiData.monthlyOnboardedDistributors.subtitle}
                    trend={kpiData.monthlyOnboardedDistributors.trend}
                    icon={<UserPlus size={22} strokeWidth={2.5} />}
                    iconBgColor="#F59E0B"
                    iconColor="#fff"
                />
            </div>

            {/* Charts Row 1: User Distribution by Role + Donut */}
            <div className="admin-charts-row">
                {/* User Distribution by Role */}
                <div className="dash-card admin-chart-main">
                    <div className="dash-card-header">
                        <h3 className="dash-section-title">User Distribution by Role</h3>
                        <p className="dash-section-sub">Active users across all roles</p>
                    </div>
                    <BarChart
                        labels={roleData.labels}
                        datasets={roleBarDatasets}
                        height={240}
                        showLegend={false}
                        yAxisLabel="Users"
                    />
                </div>

                {/* Fund Distribution */}
                <div className="dash-card admin-chart-side">
                    <div className="dash-card-header">
                        <h3 className="dash-section-title">Fund Distribution</h3>
                        <p className="dash-section-sub">Active funds with mapped classes</p>
                    </div>
                    <DonutChart
                        labels={fundData.labels}
                        data={fundData.data}
                        colors={fundData.colors}
                        centerLabel={`${fundCenterCount}\nfunds`}
                        height={175}
                        legendItems={fundData.legendItems}
                    />
                </div>
            </div>

        </div>
    );
};

export default AdminOverviewDashboard;
